"use client";

import { Crown } from "lucide-react";

import { useTranslations } from "next-intl";

import { useMemo } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { cn } from "@/shared/lib/utils";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import { medalOf, type Medal } from "@/shared/lib/medal";
import { scoreColor } from "@/shared/lib/score-color";

/**
 * KAN-806: пьедестал итогов турнира (эпик KAN-804).
 *
 * Прежний `TournamentPodium` отвечал «кто занял места». Здесь пьедестал — главный
 * экран страницы: карточка чемпиона шире остальных, у каждой команды счёт по картам
 * и средний балл состава, а внутри — состав с кольцевым баллом каждого игрока. То
 * есть на один взгляд видно не только кто выиграл, но и кем.
 *
 * Компонент ничего не запрашивает: и подиум, и баллы приходят пропами. Состояние
 * выбранного игрока живёт на странице (KAN-878), а балл — величина, которую считает
 * бэкенд (GL Score, KAN-764), и второй её источник на клиенте развёл бы цифры между
 * секциями.
 */

/** Тот же контракт, что у существующего подиума, — чтобы источник данных был один. */
export type PodiumMember = {
  userId?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  /** Роль нужна для иконки позиции и порядка строк. */
  role?: string | null;
};

export type PodiumEntry = {
  place: number;
  name: string;
  tag?: string | null;
  /** Счёт по картам строкой, как его отдаёт бэкенд: «4 : 0». */
  score?: string | null;
  /**
   * KAN-891: капитан команды. Идентификатор, а не флаг на участнике: капитан —
   * свойство команды, и один человек может капитанить в одной и играть рядовым в
   * другой. null — капитана нет (соло-турнир, драфт-команда): значок не рисуется и
   * первый в списке капитаном не назначается.
   */
  captainId?: string | null;
  members?: PodiumMember[];
};

/**
 * Ключ подписи места. Сама подпись берётся из словаря: до этой правки она была
 * русской строкой в коде, и на английской локали пьедестал оставался русским.
 */
const PLACE_KEY: Record<number, string> = {
  1: "place1",
  2: "place2",
  3: "place3",
};

/** Порядок ролей в составе — тот же, что на драфте и в карточках команд. */
const ROLE_ORDER = ["Top", "Jungle", "Mid", "Adc", "Support"] as const;
const ROLE_ORDER_INDEX: Record<string, number> = Object.fromEntries(
  ROLE_ORDER.map((r, i) => [r, i]),
);

/** Геометрия кольца балла: радиус 13 в поле 32×32 — числа из макета. */
const RING_RADIUS = 13;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function memberName(m: PodiumMember, fallback: string): string {
  return String(m.displayName ?? "").trim() || fallback;
}

/**
 * Кольцо балла. Балла нет — кольца нет вовсе: пустое кольцо с нулём читается как
 * «сыграл на ноль», хотя правда — «сравнивать не с чем» (выборка не набрана либо
 * реплеев у турнира нет).
 */
function ScoreRing({ score }: { score: number }) {
  const filled = Math.max(0, Math.min(100, score));
  const color = scoreColor(filled);

  return (
    <span className="relative block h-8 w-8 flex-none">
      <svg viewBox="0 0 32 32" className="absolute inset-0 -rotate-90">
        <circle
          cx="16"
          cy="16"
          r={RING_RADIUS}
          fill="none"
          stroke="var(--muted)"
          strokeWidth="3"
        />
        <circle
          cx="16"
          cy="16"
          r={RING_RADIUS}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${((filled / 100) * RING_CIRCUMFERENCE).toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-heading text-xs font-semibold leading-none tabular-nums">
        {Math.round(filled)}
      </span>
    </span>
  );
}

function RosterRow({
  member,
  score,
  scoreNote,
  isCaptain,
  onSelect,
}: {
  member: PodiumMember;
  score?: number | null;
  /** Почему балла нет. Показывается вместо кольца — см. комментарий у прочерка. */
  scoreNote?: string | null;
  /** KAN-891: капитан команды — значок в строке, как в макете. */
  isCaptain?: boolean;
  onSelect?: (userId: string) => void;
}) {
  const t = useTranslations("TournamentStats.podium");
  const name = memberName(member, t("unknownPlayer"));
  const selectable = Boolean(member.userId && onSelect);

  // Кликабельная строка — настоящая кнопка (Enter/Space из коробки), иначе — просто строка.
  const Row = selectable ? "button" : "div";

  return (
    <Row
      type={selectable ? "button" : undefined}
      onClick={selectable ? () => onSelect!(member.userId!) : undefined}
      className={cn(
        "-mx-2 flex items-center gap-2 rounded-[9px] px-2 py-1.5",
        selectable &&
          "w-full text-left cursor-pointer transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <span className="flex w-[18px] flex-none items-center justify-center text-muted-foreground">
        <RoleIcon role={member.role} size={16} />
      </span>
      {/* Настоящий аватар. Буква — ЗАПАСНОЙ вариант, а не основной: `/results` отдаёт
          `avatarUrl`, и до этой правки витрина его просто не читала — состав выглядел
          безликим списком инициалов при живых аватарках в ответе. */}
      <Avatar className="h-6 w-6 flex-none">
        <AvatarImage src={member.avatarUrl ?? undefined} alt={name} />
        <AvatarFallback className="bg-muted text-[10px] font-bold text-muted-foreground">
          {name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
        {name}
      </span>
      {/* Значок капитана — с подписью для тех, кто читает страницу голосом:
          корона без имени роли не сообщает ничего. */}
      {isCaptain ? (
        <Crown
          className="size-3.5 flex-none text-amber-400"
          aria-label={t("captain")}
        />
      ) : null}
      {typeof score === "number" ? (
        <ScoreRing score={score} />
      ) : (
        /* Пустое место читалось как «у игрока балла быть не должно». Прочерк с
           причиной отвечает на вопрос, который иначе задаёт себе читатель: у одних
           в составе кольцо есть, у других нет, и без объяснения это выглядит
           поломкой, а не порогом выборки. Ноль здесь ставить нельзя — «мало игр»
           и «сыграл на ноль» разные утверждения. */
        <span
          className="flex h-8 w-8 flex-none items-center justify-center text-sm text-muted-foreground"
          title={scoreNote ?? undefined}
          aria-label={scoreNote ?? undefined}
        >
          —
        </span>
      )}
    </Row>
  );
}

function PodiumCard({
  entry,
  index,
  scoreByUserId,
  scoreNoteByUserId,
  onSelectPlayer,
}: {
  entry: PodiumEntry;
  /** Позиция в сетке: нулевая карточка крупнее — она чемпион. */
  index: number;
  scoreByUserId?: Record<string, number | null | undefined>;
  /** Почему балла нет — по игрокам. Отсутствие записи даёт прочерк без пояснения. */
  scoreNoteByUserId?: Record<string, string | undefined>;
  onSelectPlayer?: (userId: string) => void;
}) {
  const t = useTranslations("TournamentStats.podium");
  const medal = medalOf(entry.place);
  const lead = index === 0;
  const placeKey = PLACE_KEY[entry.place];

  const roster = useMemo(
    () =>
      (entry.members ?? [])
        .slice()
        .sort(
          (a, b) =>
            (ROLE_ORDER_INDEX[a.role ?? ""] ?? 99) -
            (ROLE_ORDER_INDEX[b.role ?? ""] ?? 99),
        ),
    [entry.members],
  );

  const scores = roster.map((m) =>
    m.userId ? scoreByUserId?.[m.userId] : undefined,
  );

  /**
   * Средний балл состава — среднее ПОКАЗАННЫХ баллов, и только когда они есть у
   * ВСЕХ игроков карточки.
   *
   * Считать среднее по половине состава нельзя: команда, у которой два игрока не
   * набрали выборку, получила бы среднее двух оставшихся и выглядела бы сильнее или
   * слабее, чем есть. Отсутствие числа честнее числа, посчитанного не по тем людям.
   *
   * Это НЕ рейтинг силы команды из KAN-783: тот собирается из результатов и
   * командных метрик и будет другим числом. Здесь ровно то, что написано в подписи, —
   * среднее баллов пятерых, которые тут же и показаны.
   */
  const average = useMemo(() => {
    if (roster.length === 0) return null;
    const known = scores.filter((s): s is number => typeof s === "number");
    if (known.length !== roster.length) return null;
    return Math.round(known.reduce((sum, s) => sum + s, 0) / known.length);
  }, [roster.length, scores]);

  return (
    <div
      className="relative flex flex-col overflow-hidden rounded-[18px] border bg-surface-inset"
      style={{
        borderColor: "var(--border)",
        minHeight: lead ? 300 : 276,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,.03)",
      }}
    >
      {/* Свечение металла в углу: место читается раньше, чем прочитан текст. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-[90px] -top-[120px] block h-[280px] w-[280px] rounded-full"
        style={{ background: glowOf(medal, lead) }}
      />

      <div className="relative flex flex-1 flex-col gap-3.5 p-5 pb-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <PlaceDisc place={entry.place} medal={medal} lead={lead} />

          <span className="min-w-[130px] flex-1 basis-[150px]">
            <span
              className={cn(
                "block truncate font-heading font-bold leading-[1.25] tracking-[-0.015em] text-foreground",
                lead ? "text-[34px]" : "text-[25px]",
              )}
            >
              {entry.name}
            </span>
            <span
              className="mt-[3px] block font-heading text-[11px] font-bold uppercase tracking-[0.14em]"
              style={metalText(medal)}
            >
              {/* Место вне пьедестала подписывается отдельным ключом, а не склейкой
                  числа со словом: в других языках порядок слов другой. */}
              {placeKey ? t(placeKey) : t("placeOther", { place: entry.place })}
            </span>
          </span>

          <span className="ml-auto flex-none whitespace-nowrap text-right">
            {entry.score ? (
              <span className="flex items-baseline justify-end gap-1.5">
                <span
                  className={cn(
                    "font-heading font-bold leading-none tabular-nums",
                    lead ? "text-2xl" : "text-xl",
                  )}
                >
                  {entry.score}
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[0.11em] text-muted-foreground">
                  {t("maps")}
                </span>
              </span>
            ) : null}
            {average !== null ? (
              <span className="mt-[5px] block text-[11px] text-muted-foreground">
                {t("averageScore")}{" "}
                <span
                  className="font-bold tabular-nums"
                  style={{ color: scoreColor(average) }}
                >
                  {average}
                </span>
              </span>
            ) : null}
          </span>
        </div>

        {roster.length > 0 ? (
          <div className="mt-auto flex flex-col gap-px border-t border-border pt-3">
            {roster.map((member, i) => (
              <RosterRow
                key={member.userId ?? `${entry.place}-${i}`}
                member={member}
                score={scores[i]}
                scoreNote={member.userId ? scoreNoteByUserId?.[member.userId] : undefined}
                isCaptain={Boolean(
                  member.userId && entry.captainId && member.userId === entry.captainId,
                )}
                onSelect={onSelectPlayer}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Диск с номером места: металлическое кольцо, номер тем же металлом по тексту. */
function PlaceDisc({
  place,
  medal,
  lead,
}: {
  place: number;
  medal: Medal | null;
  lead: boolean;
}) {
  const size = lead ? 40 : 32;

  return (
    <span
      className="flex flex-none items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: medal ? medal.bar : "var(--muted)",
        boxShadow: medal
          ? `0 2px 10px color-mix(in srgb, ${medal.mid} ${lead ? 40 : 22}%, transparent)`
          : undefined,
      }}
    >
      <span className="flex h-[78%] w-[78%] items-center justify-center rounded-full bg-card">
        <span
          className={cn(
            "font-heading font-bold leading-none",
            lead ? "text-xl" : "text-base",
          )}
          style={metalText(medal)}
        >
          {place}
        </span>
      </span>
    </span>
  );
}

/**
 * Текст, залитый металлом. Без металла — обычный цвет: градиент по тексту у
 * четвёртого места выглядел бы наградой, которой нет.
 */
function metalText(medal: Medal | null): React.CSSProperties {
  if (!medal) return { color: "var(--muted-foreground)" };
  return {
    background: medal.bar,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
  };
}

function glowOf(medal: Medal | null, lead: boolean): string | undefined {
  if (!medal) return undefined;
  return `radial-gradient(closest-side, color-mix(in srgb, ${medal.mid} ${lead ? 17 : 9}%, transparent), transparent 78%)`;
}

export function ResultsPodium({
  entries,
  scoreByUserId,
  scoreNoteByUserId,
  onSelectPlayer,
}: {
  entries: PodiumEntry[] | null | undefined;
  /**
   * Балл игрока по его userId. Приходит снаружи: считает его бэкенд (GL Score,
   * KAN-764), и второй источник на клиенте развёл бы цифры между секциями страницы.
   * Игрока нет в карте — вместо кольца прочерк с причиной из `scoreNoteByUserId`.
   */
  scoreByUserId?: Record<string, number | null | undefined>;
  /** Почему балла нет, по игрокам. Причину знает страница — она видит метрики. */
  scoreNoteByUserId?: Record<string, string | undefined>;
  onSelectPlayer?: (userId: string) => void;
}) {
  const sorted = useMemo(
    () => (entries ?? []).slice().sort((a, b) => a.place - b.place),
    [entries],
  );

  if (sorted.length === 0) return null;

  return (
    <div
      className={cn(
        "grid items-stretch gap-4",
        // Чемпион шире остальных — это и есть пьедестал. На узком экране карточки
        // встают в колонку: сжимать три до нечитаемости хуже, чем пролистать.
        sorted.length >= 3
          ? "sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr]"
          : sorted.length === 2
            ? "sm:grid-cols-2"
            : "grid-cols-1",
      )}
    >
      {sorted.map((entry, i) => (
        <PodiumCard
          key={`${entry.place}-${entry.name}`}
          entry={entry}
          index={i}
          scoreByUserId={scoreByUserId}
          scoreNoteByUserId={scoreNoteByUserId}
          onSelectPlayer={onSelectPlayer}
        />
      ))}
    </div>
  );
}
